import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { getUserAvatarColor } from "../../../utils/kpiColors";

const KPILeaderboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { leaderboard, teamReview, loading } = useSelector((state) => state.kpi);
  const userInfoData = useSelector((state) => state.auth?.userInfoData);
  const userInfo = useSelector((state) => state.auth?.userInfo);

  const loggedInUser = userInfoData || userInfo?.userInfo || userInfo || {};
  const currentUserId = loggedInUser._id || loggedInUser.id || "";
  const currentUserEmail = (loggedInUser.email || "").toLowerCase().trim();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("ALL");

  useEffect(() => {
    dispatch.kpi.getLeaderboard();
    dispatch.kpi.getTeamReview();
  }, [dispatch]);

  const teamMetrics = teamReview || {};
  
  // Extract leader list from leaderboard state OR teamReview.ranking
  const rawLeaderboard = (Array.isArray(leaderboard) && leaderboard.length > 0)
    ? leaderboard
    : (Array.isArray(teamReview?.ranking) ? teamReview.ranking : []);

  // Helper to extract clean full name with email fallback
  const getLeaderFullName = (leader = {}) => {
    if (leader.full_name && leader.full_name.trim()) return leader.full_name.trim();
    const first = leader.first_name || "";
    const last = leader.last_name || "";
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
    if (leader.name && leader.name.trim()) return leader.name.trim();
    if (leader.email && leader.email.includes("@")) {
      const username = leader.email.split("@")[0];
      return username
        .replace(/[._-]/g, " ")
        .split(" ")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
        .join(" ")
        .replace(/\s+tblr$/i, "")
        .trim();
    }
    return "Team Member";
  };

  // Filter and sort list by rank & total KPI points earned
  const filteredLeaderboard = React.useMemo(() => {
    const list = rawLeaderboard.filter((leader) => {
      const fullName = getLeaderFullName(leader);
      const role = leader.designation || leader.designation_details?.name || leader.role || "";
      
      const matchesSearch = fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            role.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRole = filterRole === "ALL" || role.toLowerCase().includes(filterRole.toLowerCase());

      return matchesSearch && matchesRole;
    });

    return list.sort((a, b) => {
      const rA = a.rank !== undefined && a.rank !== null ? Number(a.rank) : 9999;
      const rB = b.rank !== undefined && b.rank !== null ? Number(b.rank) : 9999;
      if (rA !== rB) return rA - rB;

      const pA = a.total_points !== undefined ? Number(a.total_points) : (a.total_score !== undefined ? Number(a.total_score) : (a.monthly_score || 0));
      const pB = b.total_points !== undefined ? Number(b.total_points) : (b.total_score !== undefined ? Number(b.total_score) : (b.monthly_score || 0));
      return pB - pA;
    });
  }, [rawLeaderboard, searchTerm, filterRole]);

  return (
    <div className="w-full">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-4">
        <Link to="/kpi/dashboard" className="hover:text-slate-900 transition-colors">
          Dashboard
        </Link>
        <span>/</span>
        <span className="text-slate-900 font-semibold">Leaderboard</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-slate-900">Team Leaderboard</h1>
            <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-bold font-mono">
              {rawLeaderboard.length} leaders
            </span>
          </div>
          <p className="text-slate-500 text-sm">
            Rankings and performance metrics across all active team members
          </p>
        </div>

        <button
          onClick={() => navigate("/kpi/dashboard")}
          className="flex items-center gap-2 px-3.5 py-2 border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
          Back to Dashboard
        </button>
      </div>

      {/* Leaderboard Table Container */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
        {/* Controls Bar */}
        <div className="p-6 border-b border-slate-100/90 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 ">
          <div>
            <h2 className="text-base font-bold text-slate-900 tracking-tight">Full Leaderboard</h2>
            <p className="text-xs text-slate-400 font-medium mt-0.5">Ranked by total net KPI points earned</p>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            {/* Search Input */}
            <div className="relative flex-1 md:w-64">
              <svg className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input
                type="text"
                placeholder="Search leader or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none focus:border-slate-900 bg-white"
              />
            </div>
          </div>
        </div>

        {/* Leaderboard Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100/60">
                <th className="px-6 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">Rank</th>
                <th className="px-6 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">Leader</th>
                <th className="px-6 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">Role / Designation</th>
                <th className="px-6 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono text-right">KPI Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredLeaderboard.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-sm text-slate-400 font-medium">
                    No matching leaders found.
                  </td>
                </tr>
              ) : (
                filteredLeaderboard.map((leader, idx) => {
                  const rankNum = leader.rank !== undefined ? leader.rank : (idx + 1);
                  const fullName = getLeaderFullName(leader);
                  const parts = fullName.split(" ").filter(Boolean);
                  const initials = parts.length >= 2
                    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
                    : (fullName.slice(0, 2) || "TM").toUpperCase();

                  const role = leader.designation || leader.designation_details?.name || leader.role || "Team Member";
                 const ptsVal = leader.monthly_score ?? 0;

                  const isCurrentUser = Boolean(
                    leader.is_current_user ||
                    leader.isCurrentUser ||
                    (currentUserId && String(leader.user_id || leader._id || leader.id || "") === String(currentUserId)) ||
                    (currentUserEmail && (leader.email || "").toLowerCase().trim() === currentUserEmail)
                  );
                  const avatarColorClass = getUserAvatarColor(leader.user_id || leader._id || fullName, idx);

                  return (
                    <tr
                      key={leader.user_id || leader._id || idx}
                      className={`transition-colors ${
                        isCurrentUser ? "bg-amber-50/90 font-semibold border-l-4 border-amber-500 shadow-2xs" : "hover:bg-slate-50/70"
                      }`}
                    >
                      {/* Rank Indicator */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {rankNum === 1 ? (
                            <div className="w-7 h-7 rounded-full bg-slate-900 text-amber-400 flex items-center justify-center text-xs font-bold shadow-2xs">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
                              </svg>
                            </div>
                          ) : rankNum <= 3 ? (
                            <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-800 flex items-center justify-center text-xs font-extrabold font-mono border border-slate-200">
                              #{rankNum}
                            </div>
                          ) : (
                            <span className="text-xs font-bold text-slate-400 font-mono w-7 text-center">
                              #{rankNum}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Leader Info */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {leader.profile_picture ? (
                            <img src={leader.profile_picture} alt={fullName} className="w-9 h-9 rounded-full object-cover shrink-0 border border-slate-200 shadow-2xs" />
                          ) : (
                            <div className={`w-9 h-9 rounded-full ${avatarColorClass} text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-2xs`}>
                              {initials}
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
                              <span>{fullName}</span>
                              {isCurrentUser && (
                                <span className="px-2 py-0.5 rounded-full bg-slate-900 text-amber-300 text-[10px] font-black uppercase tracking-wider shadow-2xs">
                                  YOU
                                </span>
                              )}
                            </div>
                            {leader.email && (
                              <div className="text-xs text-slate-400 font-normal">{leader.email}</div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td className="px-6 py-4 text-xs font-semibold text-slate-600">
                        {role}
                      </td>

                      {/* KPI Score */}
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-900 text-white text-xs font-bold font-mono shadow-2xs">
                          {ptsVal} pts
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KPILeaderboard;
